import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchLandlordRentLeases,
  formatRentAmount,
  formatRentDate,
  getRentStatusInfo,
  resolveLeaseScreeningId,
} from '../../Services/rentService';
import '../../Components/Lease/LeaseAgreement.css';

const PAGE_SIZE = 10;

const imageUrl = (path) => (window.getPublicUrl ? window.getPublicUrl(path) : path);

const Leases = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [leases, setLeases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const searchTimeoutRef = useRef(null);

  // Debounce the search box so every keystroke does not re-filter the list.
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const loadLeases = useCallback(async () => {
    setIsLoading(true);
    try {
      const rows = await fetchLandlordRentLeases();
      setError(null);
      setLeases(rows);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load leases.';
      console.error('Error loading leases:', err);
      setError(message);
      setLeases([]);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeases();
  }, [loadLeases]);

  // /rent/landlord-leases takes no query parameters, so searching and paging
  // both happen here on the rows the one call returned.
  const filteredLeases = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return leases;
    return leases.filter((lease) =>
      [
        lease.propertyAddress,
        lease.propertyCityState,
        lease.propertyCode,
        lease.tenantName,
        lease.tenantEmail,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    );
  }, [leases, debouncedSearch]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [debouncedSearch, leases]);

  const visibleLeases = filteredLeases.slice(0, visibleCount);
  const hasMore = visibleCount < filteredLeases.length;

  // Word files never render inline, so every document opens in a new tab.
  const handleViewDocument = (lease) => {
    if (!lease.documentUrl) {
      toast.info('This lease has no document to view yet.');
      return;
    }
    window.open(lease.documentUrl, '_blank', 'noopener,noreferrer');
  };

  // The tenant detail page is keyed on the screening, not the tenant's user id.
  const handleRowNavigate = async (lease) => {
    if (!lease.propertyId) return;
    try {
      const screeningId = await resolveLeaseScreeningId(lease);
      if (screeningId) {
        navigate(`/landlord/properties/${lease.propertyId}/tenant/${screeningId}`);
      } else {
        toast.info("This tenant's application could not be found.");
      }
    } catch (error) {
      console.error('Error opening tenant details:', error);
      toast.error(error?.response?.data?.message || 'Failed to open tenant details.');
    }
  };

  // The lease detail page shows the property and tenant with this property's
  // rent payment history below them.
  const handleViewDetails = (lease) => {
    if (!lease.id) {
      toast.info('This lease has no details to show yet.');
      return;
    }
    navigate(`/landlord/leases/${lease.id}`, { state: { lease } });
  };

  const renderBody = () => {
    if (isLoading) {
      return <div className="leases-placeholder">Loading leases...</div>;
    }
    if (error) {
      return <div className="leases-placeholder leases-placeholder--error">{error}</div>;
    }
    if (filteredLeases.length === 0) {
      return (
        <div className="leases-placeholder">
          {debouncedSearch.trim()
            ? 'No leases match your search.'
            : 'No leases yet. Send a lease from a tenant’s detail page and it will appear here.'}
        </div>
      );
    }

    return (
      <table className="screenings-table leases-table">
        <thead>
          <tr>
            <th>Sr. no.</th>
            <th>Property</th>
            <th>Tenant</th>
            <th>Monthly Rent</th>
            <th>Next Payment</th>
            <th>Lease Document</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {visibleLeases.map((lease, index) => {
            const statusInfo = getRentStatusInfo(lease.status);
            const documentTitle = lease.documentTitle || 'Lease Agreement';
            return (
              <tr key={lease.id ?? `${lease.propertyId}-${lease.tenantId}-${index}`}>
                <td>{index + 1}</td>
                <td className="screenings-cell-address">
                  <span className="screenings-cell-address-text">{lease.propertyAddress || '—'}</span>
                  {lease.propertyCode ? (
                    <span className="leases-cell-sub">Code: {lease.propertyCode}</span>
                  ) : null}
                </td>
                <td className="screenings-cell-address">
                  <span className="screenings-cell-address-text">
                    {lease.tenantName ? (
                      <button
                        type="button"
                        className="leases-link-button"
                        onClick={() => handleRowNavigate(lease)}
                        disabled={!lease.propertyId || !lease.tenantId}
                      >
                        {lease.tenantName}
                      </button>
                    ) : (
                      '—'
                    )}
                  </span>
                  {lease.tenantEmail ? (
                    <span className="leases-cell-sub">{lease.tenantEmail}</span>
                  ) : null}
                </td>
                <td>{formatRentAmount(lease.monthlyRent)}</td>
                <td>{formatRentDate(lease.nextPaymentDate)}</td>
                <td className="screenings-cell-address">
                  <div className="leases-document-cell">
                    <span className="screenings-cell-address-text" title={documentTitle}>
                      {documentTitle}
                    </span>
                    {lease.documentUrl ? (
                      <button
                        type="button"
                        className="leases-document-view"
                        onClick={() => handleViewDocument(lease)}
                        aria-label={`View ${documentTitle}`}
                      >
                        <img src={imageUrl('/assets/images/eye-open_new.png')} alt="" />
                      </button>
                    ) : null}
                  </div>
                  {lease.fileName ? <span className="leases-cell-sub">{lease.fileName}</span> : null}
                </td>
                <td>
                  <span className={`lease-badge lease-badge--${statusInfo.variant}`}>
                    {statusInfo.label}
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="screenings-view-detail-button"
                    onClick={() => handleViewDetails(lease)}
                    disabled={!lease.id}
                  >
                    View details
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="screenings-page">
      <div className="screenings-actions-bar">
        <div className="screenings-search-wrapper">
          <img
            src={imageUrl('/assets/images/SearchIcon.svg')}
            alt="Search"
            className="screenings-search-icon"
          />
          <input
            type="text"
            className="screenings-search-input"
            placeholder="Search by property or tenant"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <h2 className="screenings-title">
        Leases{!isLoading && !error ? ` (${filteredLeases.length})` : ''}
      </h2>

      <div className="screenings-content">{renderBody()}</div>

      {hasMore && !isLoading && !error ? (
        <div className="leases-load-more-row">
          <button
            type="button"
            className="lease-btn lease-btn--secondary"
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
          >
            Load More
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default Leases;
